/* eslint-disable react/no-array-index-key, @typescript-eslint/naming-convention  */
import {
  IonCol, IonContent, IonRow, IonLabel, IonButton,
} from '@ionic/react'
import { Icon } from '@iconify/react'
import React from 'react'
import { filterJourneysForUser } from '../../helpers/journeyFilters'
import moment from 'moment'
import { WidgetContainer, WidgetTitles, ViewButton } from '../maps/WidgetContainers'

import { useAuth } from '../../hooks/useAuth'
import { useJourneysData2 } from '../../hooks/journeys/useJourneysData2'
import { JourneyStatus } from '../types/GlobalTypes'
import { Journey, AirJourney } from '../types/OptimizedMaps'
import { mapJourneyToRow } from './JourneyRowUtils'

const JourneysList = () => {
  const auth = useAuth()

  /* Current logged in user's id */
  /* @typescript-eslint/naming-convention */
  const { user_id } = auth.user

  const {
    view, loading, showAirJourneyForm, showForm,
    journeys, airJourneys, handleAirJourneyClick,
  } = useJourneysData2()

  const getColor = (index: number) => ((index % 2 === 0) ? 'rgba(65, 151, 169, 0.25)' : 'white')

  /* Returns the color related to a journey status */
  function getStatusColor(status: JourneyStatus | 'Approved') {
    let color = ''
    switch (status) {
      case 'Pending':
        color = '#326771'
        break
      case 'Pre-Departure':
        color = '#4197A9'
        break
      case 'In-Transit':
        color = '#FF8A00'
        break
      case 'Approved':
        color = '#14C105'
        break
      case 'Complete':
        color = '#0C9500'
        break
      default:
        color = ''
        break
    }
    return color
  }

  const formatDisplayDate = (date?: string) => (date ? moment(date).format('DD/MM/YY') : '')

  const getViewLabel = (journey: { approverId: number; status: JourneyStatus | 'Approved' }) => (
    journey.approverId === user_id && journey.status === 'Pending' ? 'Authorise' : 'View'
  )

  function sortJourneysByDepartureDateAscending(journeyList: Journey[]) {
    journeyList.sort(
      (a, b) => new Date(a.departure_time).getTime() - new Date(b.departure_time).getTime(),
    )
    return journeyList
  }

  function sortJourneysByDepartureDateDescending(journeyList: Journey[]) {
    sortJourneysByDepartureDateAscending(journeyList)
    return journeyList.reverse()
  }

  // Personal journeys should only include journeys created or led by the
  // current user.
  const userJourneys = filterJourneysForUser(user_id, journeys)
  const userAirJourneys = filterJourneysForUser(user_id, airJourneys as unknown as Journey[])

  const userRoadRows = userJourneys.map((journey) => ({
    ...mapJourneyToRow(journey, false),
    onClick: () => showForm(journey),
  }))

  const managerRoadRows = journeys.map((journey) => ({
    ...mapJourneyToRow(journey, true),
    onClick: () => showForm(journey),
  }))

  const userAirRows = (userAirJourneys as unknown as AirJourney[]).map((airJourney) => ({
    ...mapJourneyToRow(airJourney, false),
    onClick: () => handleAirJourneyClick(airJourney),
  }))

  const managerAirRows = (airJourneys as unknown as AirJourney[]).map((airJourney) => ({
    ...mapJourneyToRow(airJourney, true),
    onClick: () => handleAirJourneyClick(airJourney),
  }))

  const combinedJourneys = React.useMemo(() => {
    const all = [...userRoadRows, ...userAirRows]
    return all.sort((a, b) => new Date(b.departureDate).getTime() - new Date(a.departureDate).getTime())
  }, [userRoadRows, userAirRows])

  const combinedJourneysManager = React.useMemo(() => {
    const all = [...managerRoadRows, ...managerAirRows]
    return all.sort((a, b) => new Date(b.departureDate).getTime() - new Date(a.departureDate).getTime())
  }, [managerRoadRows, managerAirRows])

  return (
    <IonRow
      style={{
        display: (view === 'Air Travel' || showAirJourneyForm) ? 'none' : 'flex',
        justifyContent: 'center',
        height: '100%',
      }}
    >
      <WidgetContainer style={{ '--ion-background-color': 'white', zIndex: 11 }}>
        {
            loading
            && (
              <p>Loading ...</p>
            )
          }
        {
          !loading && view === 'Personal Journeys'
          && (
            <>
              <WidgetTitles style={{ paddingRight: '0', height: '10%' }}>
                <IonRow class='ion-align-items-center' style={{ height: '100%', padding: '0' }} className='ion-align-items-center'>
                  <IonCol style={{ fontWeight: 'bold' }} size='2'> Start </IonCol>
                  <IonCol style={{ fontWeight: 'bold' }} size='2'> End </IonCol>
                  <IonCol style={{ fontWeight: 'bold' }} size='2'> Requested by </IonCol>
                  <IonCol class='ion-align-items-center' style={{ fontWeight: 'bold' }} size='2'>
                    <IonLabel style={{ marginTop: '0.5em' }}>Departure Date</IonLabel>
                    <IonButton
                      onClick={sortJourneysByDepartureDateDescending(journeys)}
                      size='small'
                      fill='clear'
                      style={{ color: '#326771', paddingBottom: '0.9em' }}
                    >
                      <Icon icon='bxs:down-arrow' />
                    </IonButton>
                  </IonCol>
                  <IonCol style={{ fontWeight: 'bold' }} size='1'> Arrival Date </IonCol>
                  <IonCol style={{ fontWeight: 'bold' }} size='1'> Status </IonCol>
                  <IonCol style={{ fontWeight: 'bold' }} size='1'> Travel Type </IonCol>
                  <IonCol size='1' />
                </IonRow>
              </WidgetTitles>
              <IonContent style={{ height: '90%', '--color': '#326771', position: 'relative' }}>
                {
                  combinedJourneys.map((journey, index) => (
                    <IonRow style={{ backgroundColor: getColor(index), height: '10%', fontSize: '.8rem' }} className='ion-align-items-center widget-row ion-text-center' key={journey.id}>
                      <IonCol className='journey-start-location' size='2'> { journey.start } </IonCol>
                      <IonCol className='journey-end-location' size='2'> { journey.end } </IonCol>
                      <IonCol size='2'> { journey.submitterName } </IonCol>
                      <IonCol size='2'> { formatDisplayDate(journey.departureDate) } </IonCol>
                      <IonCol size='1'> { formatDisplayDate(journey.arrivalDate) } </IonCol>
                      <IonCol size='1'>
                        <IonLabel style={{
                          backgroundColor: getStatusColor(journey.status), color: '#FFFFFF', padding: '0.3em', borderRadius: '0.2em',
                        }}
                        >
                          {journey.status}
                        </IonLabel>
                      </IonCol>
                      <IonCol size='1'> { journey.travelType } </IonCol>
                      <IonCol size='1'>
                        <ViewButton id={`view-journey-${journey.start}`} onClick={journey.onClick}>
                          {getViewLabel(journey)}
                        </ViewButton>
                      </IonCol>
                    </IonRow>
                  ))
                }
                {
                  journeys.length < 3 && (
                    [...Array(3 - journeys.length)].map((val, index) => (
                      <IonRow style={{ backgroundColor: getColor(journeys.length + index), height: '25%' }} key={index} />
                    ))
                  )
                }
              </IonContent>
            </>
          )
        }
        {
          !loading && view === 'Managed Journeys'
          && (
            <>
              <WidgetTitles style={{ paddingRight: '0', height: '10%' }}>
                <IonRow class='ion-align-items-center' style={{ height: '100%', padding: '0' }} className='ion-align-items-center'>
                  <IonCol style={{ fontWeight: 'bold' }} size='2'> Start </IonCol>
                  <IonCol style={{ fontWeight: 'bold' }} size='2'> End </IonCol>
                  <IonCol style={{ fontWeight: 'bold' }} size='2'> Requested by </IonCol>
                  <IonCol class='ion-align-items-center' style={{ fontWeight: 'bold' }} size='2'>
                    <IonLabel style={{ marginTop: '0.5em' }}>Departure Date</IonLabel>
                    <IonButton
                      onClick={sortJourneysByDepartureDateDescending(journeys)}
                      size='small'
                      fill='clear'
                      style={{ color: '#326771', paddingBottom: '0.9em' }}
                    >
                      <Icon icon='bxs:down-arrow' />
                    </IonButton>
                  </IonCol>
                  <IonCol style={{ fontWeight: 'bold' }} size='1'> Arrival Date </IonCol>
                  <IonCol style={{ fontWeight: 'bold' }} size='2'> Status </IonCol>
                  <IonCol size='1' />
                </IonRow>
              </WidgetTitles>
              <IonContent style={{ height: '90%', '--color': '#326771', position: 'relative' }}>
                {
                  combinedJourneysManager.map((journey, index) => (
                    <IonRow style={{ backgroundColor: getColor(index), height: '10%', fontSize: '.8rem' }} className='ion-align-items-center widget-row ion-text-center' key={journey.id}>
                      <IonCol className='journey-start-location' size='2'> { journey.start } </IonCol>
                      <IonCol className='journey-end-location' size='2'> { journey.end } </IonCol>
                      <IonCol size='2'> { journey.submitterName } </IonCol>
                      <IonCol size='2'> { formatDisplayDate(journey.departureDate) } </IonCol>
                      <IonCol size='1'> { formatDisplayDate(journey.arrivalDate) } </IonCol>
                      <IonCol size='1'>
                        <IonLabel style={{
                          backgroundColor: getStatusColor(journey.status), color: '#FFFFFF', padding: '0.3em', borderRadius: '0.2em',
                        }}
                        >
                          {journey.status}
                        </IonLabel>
                      </IonCol>
                      <IonCol size='1'> { journey.travelType } </IonCol>
                      <IonCol size='1'>
                        <ViewButton id={`view-journey-${journey.start}`} onClick={journey.onClick}>
                          {getViewLabel(journey)}
                        </ViewButton>
                      </IonCol>
                    </IonRow>
                  ))
                }
                {
                  journeys.length < 3 && (
                    [...Array(3 - journeys.length)].map((val, index) => (
                      <IonRow style={{ backgroundColor: getColor(journeys.length + index), height: '25%' }} key={index} />
                    ))
                  )
                }
              </IonContent>
            </>
          )
        }
      </WidgetContainer>
    </IonRow>
  )
}

export default JourneysList
